import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { AddLayerService, FluxLayerType } from '@connect-services/client/services/add-layer.service';
import { AddLayerFluxService } from '@connect-services/client/services/add-layer-flux.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { UrlSourceEnum } from '@connect-services/client/services/url-source.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    ServiceCatalog,
    ServiceCatalogControllerGetRequestParams,
    ServiceCatalogsService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { OrderEnum } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

type ServiceCatalogByTopic = {
    serviceCatalogs: ServiceCatalog[];
    topicName: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        ExpansionPanelComponent,
        InputTextComponent,
        LoadingComponent,
        RessourcesModule,
        SelectComponent,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-select-flux-step',
    templateUrl: './select-flux-step.component.html',
})
export class SelectFluxStepComponent implements OnInit {
    public readonly isGetLayersFromUrlLoading = signal(false);
    public readonly searchInput = input<string>('');
    public readonly selectedLayerType = input<FluxLayerType | undefined>(undefined);

    protected readonly addLayerFluxService = inject(AddLayerFluxService);
    protected readonly addLayerService = inject(AddLayerService);
    protected readonly CheckboxState = CheckboxState;
    protected readonly fluxTypeOptions: SelectOption<FluxLayerType>[] = [
        { label: 'WFS', value: UrlSourceEnum.WFS },
        { label: 'WMS', value: UrlSourceEnum.WMS },
    ];
    protected readonly isLoading = signal(false);
    protected readonly isManualSelectionActive = computed(() => !!this.manualSourceUrl().trim());
    protected readonly manualSourceUrl = signal('');
    protected readonly selectedFluxTypeOption = computed(() =>
        this.addLayerService.getDefaultValue(this.fluxTypeOptions, this.addLayerService.selectedFluxType()),
    );
    protected readonly selectedServiceCatalogIds = signal<Set<string>>(new Set());
    protected readonly selectedTabIndex = signal(0);
    protected readonly serviceCatalogs = signal<ServiceCatalog[]>([]);
    protected readonly serviceCatalogsByTopic = computed<ServiceCatalogByTopic[]>(() =>
        this.groupServiceCatalogsByTopic(
            this.serviceCatalogs(),
            this.searchInput(),
            this.translocoService.translate('other'),
        ),
    );
    protected readonly tabsBadge = computed<boolean[]>(() => {
        const searchTerm = this.searchInput().trim();

        if (!searchTerm) {
            return this.serviceCatalogsByTopic().map(() => false);
        }

        return this.serviceCatalogsByTopic().map((item) => !!item.serviceCatalogs.length);
    });
    protected readonly themeTabs = computed<TabWithState[]>(() =>
        this.serviceCatalogsByTopic().map((item: ServiceCatalogByTopic) => ({
            active: !this.isManualSelectionActive(),
            name: item.topicName,
        })),
    );

    private readonly fetchAllService = inject(FetchAllService);
    private readonly serviceCatalogsService = inject(ServiceCatalogsService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public async ngOnInit(): Promise<void> {
        const layerType = this.selectedLayerType();
        if (layerType) {
            this.addLayerFluxService.selectedFluxType.set(layerType);
        }

        this.manualSourceUrl.set(this.addLayerFluxService.sourceUrlControl?.value ?? '');
        this.selectedServiceCatalogIds.set(
            new Set(this.addLayerFluxService.selectedServiceCatalogs().map((item) => item.id)),
        );

        await this.getServiceCatalogs();
    }

    public async onValueChange(url: string): Promise<void> {
        this.manualSourceUrl.set(url);

        if (this.addLayerFluxService.selectedServiceCatalogs().length) {
            return;
        }

        this.resetFluxSelectionData();

        if (!url.trim()) {
            this.addLayerFluxService.sourceUrlControl?.setErrors(null);

            return;
        }

        try {
            this.isGetLayersFromUrlLoading.set(true);
            await this.addLayerFluxService.loadAvailableSourceLayers(url);
        } catch (error: unknown) {
            this.addLayerFluxService.sourceUrlControl!.setErrors({
                invalid: error instanceof Error ? error.message : 'errorDefault',
            });
        } finally {
            this.isGetLayersFromUrlLoading.set(false);
        }
    }

    protected changeTab(index: number): void {
        this.selectedTabIndex.set(index);
    }

    protected isSelected(serviceCatalogId: string): boolean {
        return this.selectedServiceCatalogIds().has(serviceCatalogId);
    }

    protected selectFluxType(selectedLayerTypes: FluxLayerType[]): void {
        if (this.addLayerFluxService.selectedServiceCatalogs().length) {
            return;
        }

        this.addLayerFluxService.selectedFluxType.set(selectedLayerTypes[0]);
        this.resetFluxSelectionData();

        if (this.addLayerFluxService.sourceUrlControl?.value.trim()) {
            void this.onValueChange(this.addLayerFluxService.sourceUrlControl.value);
        }
    }

    protected toggleServiceCatalogSelected(serviceCatalog: ServiceCatalog): void {
        if (this.isManualSelectionActive()) {
            return;
        }

        const nextSelectedIds = new Set(this.selectedServiceCatalogIds());

        if (nextSelectedIds.has(serviceCatalog.id)) {
            nextSelectedIds.delete(serviceCatalog.id);
        } else {
            nextSelectedIds.add(serviceCatalog.id);
        }

        this.selectedServiceCatalogIds.set(nextSelectedIds);

        const selectedServiceCatalogs = this.serviceCatalogs().filter((item) => nextSelectedIds.has(item.id));

        this.addLayerService.selectServiceCatalogs(selectedServiceCatalogs);
    }

    private async getServiceCatalogs(): Promise<void> {
        this.isLoading.set(true);
        try {
            const serviceCatalogs = await this.fetchAllService.getAll<
                ServiceCatalogControllerGetRequestParams,
                ServiceCatalog
            >(this.serviceCatalogsService.serviceCatalogControllerGet.bind(this.serviceCatalogsService), {
                order: OrderEnum.ASC,
                orderBy: 'serviceTopic',
            });

            this.serviceCatalogs.set(serviceCatalogs);
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }

    private groupServiceCatalogsByTopic(
        serviceCatalogs: ServiceCatalog[],
        searchTerm: string,
        otherLabel: string,
    ): ServiceCatalogByTopic[] {
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();
        const groupedCatalogs = serviceCatalogs.reduce((acc: Record<string, ServiceCatalog[]>, serviceCatalog) => {
            const topicName = serviceCatalog.serviceTopic || otherLabel;
            const matchesSearch =
                !normalizedSearchTerm ||
                serviceCatalog.layerName.toLowerCase().includes(normalizedSearchTerm) ||
                serviceCatalog.serviceType.toLowerCase().includes(normalizedSearchTerm);

            if (!matchesSearch) {
                return acc;
            }

            if (!acc[topicName]) {
                acc[topicName] = [];
            }

            acc[topicName].push(serviceCatalog);

            return acc;
        }, {});

        return Object.entries(groupedCatalogs)
            .sort(([topicA], [topicB]) => topicA.localeCompare(topicB))
            .map(([topicName, groupedServiceCatalogs]: [string, ServiceCatalog[]]) => ({
                serviceCatalogs: groupedServiceCatalogs,
                topicName,
            }));
    }

    private resetFluxSelectionData(): void {
        this.addLayerFluxService.availableSourceLayers.set([]);
        this.addLayerFluxService.clearResolutionState();
        this.addLayerService.tableOptions.set([]);
        this.addLayerService.selectedTablesInfo.set([]);
    }
}
